import { useTranslation } from 'react-i18next';

import { customRound, displayQuarter, europeanFormatNumber } from '@/helpers';
import { Icon, Typography, Button } from '@/components';
import { useDevelopmentStatus } from '@/hooks';
import { isItemNull } from '@/features/projects/helpers';

export type BessBoxProps = {
  readonly normalizedProject: any;
  readonly onEdit?: () => void;
  readonly isEditable: boolean;
};

export function BessBox({ normalizedProject, isEditable, onEdit }: BessBoxProps) {
  const { t } = useTranslation(['opportunities']);
  const { map: mapDevelopmentStatus } = useDevelopmentStatus();
  const bessTech = normalizedProject.technologies.find((tech: any) => tech.type === 'bess');
  if (!bessTech) return null;

  const item = bessTech.details.item;
  const status = bessTech.details.developmentStatus.status;
  if (!isEditable && !status && isItemNull(item)) return null;

  const roundedInstalledCapacity = customRound(item.installedCapacity);
  const roundedInjectionCapacity = customRound(item.injectionCapacity);
  const roundedConsumptionCapacity = customRound(item.consumptionCapacity);
  const roundedStorageCapacity = customRound(item.storageCapacity);

  const fields = [
    {
      label: t('Installed Capacity'),
      value: item.installedCapacity ? `${europeanFormatNumber(roundedInstalledCapacity)} MW` : null,
    },
    {
      label: t('Hours Cycles'),
      value: item.hoursCycles ? `${europeanFormatNumber(item.hoursCycles)} h` : null,
    },
    {
      label: t('Development Status'),
      value: mapDevelopmentStatus(status) || null,
    },
    {
      label: t('Ready to Build (RtB) Date'),
      value: item.readyToBuild ? displayQuarter(item.readyToBuild) : null,
    },
    {
      label: t('Commercial Operation Date (COD)'),
      value: item.commercialOperationDate ? displayQuarter(item.commercialOperationDate) : null,
    },
    {
      label: t('Injection Capacity'),
      value: item.injectionCapacity ? `${europeanFormatNumber(roundedInjectionCapacity)} MW` : null,
    },
    {
      label: t('Consumption Capacity'),
      value: item.consumptionCapacity ? `${europeanFormatNumber(roundedConsumptionCapacity)} MW` : null,
    },
    {
      label: t('Storage Capacity'),
      value: item.storageCapacity ? `${europeanFormatNumber(roundedStorageCapacity)} MWh` : null,
    },
    {
      label: t('Storage Technology'),
      value: item.storageTechnology ?? null,
    },
  ];

  const visibleFields = isEditable
    ? fields
    : fields.filter(({ value }) => value !== null && value !== undefined && value !== '');

  return (
    <div className="rounded-lg border border-neutral-50 bg-white p-6 shadow-xs">
      <div className="mb-6 flex items-center">
        <div className="flex grow items-center">
          <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
            <Icon name="bess" className="h-6 w-6" />
          </div>
          <Typography as="h3" weight="semibold" size="title">
            {t('BESS Details & Design')}
          </Typography>
        </div>
        {isEditable && (
          <Button size="sm" color="neutral" icon="edit" onClick={onEdit} className="ml-auto">
            {t('Edit')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1">
        {visibleFields.map(({ label, value }) => (
          <Typography key={label} as="p" size="body-2" weight="semibold" relaxed className="mb-1">
            {label}:{' '}
            <span className="ml-3 text-base font-normal text-neutral-900">{value ?? (isEditable ? '—' : null)}</span>
          </Typography>
        ))}
      </div>

      {(isEditable || item.generalComments) && (
        <Typography as="p" size="body-2" weight="semibold" relaxed>
          {t('Other Comments')}:{' '}
          <span className="ml-3 text-base font-normal text-neutral-900">
            {item.generalComments ?? (isEditable ? '—' : null)}
          </span>
        </Typography>
      )}
    </div>
  );
}
